import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  AVAILABLE_SCENARIO_IDS,
  DEFAULT_SCENARIO_ID,
  getScenario,
} from "./scenarios";
import type { Scenario, ScenarioId } from "./types";

interface ScenarioContextValue {
  scenarioId: ScenarioId;
  scenario: Scenario;
  setScenarioId: (id: ScenarioId) => void;
  available: ScenarioId[];
  /** When true, the picker should disable scenario changes. */
  pinned: boolean;
}

const ScenarioContext = createContext<ScenarioContextValue | null>(null);

interface ScenarioProviderProps {
  /** Initial scenario when not pinned. Defaults to interview-segment. */
  initialId?: ScenarioId;
  /** When set, the scenario is fixed and the picker is read-only. */
  pinnedId?: ScenarioId;
  children: ReactNode;
}

export function ScenarioProvider({
  initialId,
  pinnedId,
  children,
}: ScenarioProviderProps) {
  const startId: ScenarioId = pinnedId ?? initialId ?? DEFAULT_SCENARIO_ID;
  const [scenarioId, setScenarioIdState] = useState<ScenarioId>(startId);

  const setScenarioId = useCallback(
    (id: ScenarioId) => {
      if (pinnedId) return;
      if (!AVAILABLE_SCENARIO_IDS.includes(id)) return;
      setScenarioIdState(id);
    },
    [pinnedId],
  );

  const value = useMemo<ScenarioContextValue>(
    () => ({
      scenarioId,
      scenario: getScenario(scenarioId),
      setScenarioId,
      available: AVAILABLE_SCENARIO_IDS,
      pinned: Boolean(pinnedId),
    }),
    [scenarioId, setScenarioId, pinnedId],
  );

  return <ScenarioContext.Provider value={value}>{children}</ScenarioContext.Provider>;
}

/**
 * Read the active scenario. When called outside a `<ScenarioProvider>`,
 * returns the default scenario in non-pinned mode with a no-op setter,
 * so single-panel embeds (slides, decks) keep working without a wrapping
 * provider.
 */
export function useScenario(): ScenarioContextValue {
  const ctx = useContext(ScenarioContext);
  if (ctx) return ctx;
  return {
    scenarioId: DEFAULT_SCENARIO_ID,
    scenario: getScenario(DEFAULT_SCENARIO_ID),
    setScenarioId: () => undefined,
    available: AVAILABLE_SCENARIO_IDS,
    pinned: false,
  };
}
